<template>
  <div>
    <!-- Minimized Chatbot Icon -->
    <div v-if="isCollapsed" class="chatbot-minimized" @click="toggleCollapse">
      <i class="bi bi-chat-dots-fill chatbot-minimized-icon"></i>
    </div>

    <!-- Chatbot Window -->
    <div
      v-else
      class="chatbot-container"
      :class="{ 'is-fullscreen': isFullscreen }"
      :style="chatbotStyle"
      @mousedown.stop
    >
      <div class="card chatbot-wrapper">
        <!-- Chatbot Header -->
        <div
          class="card-header chatbot-header d-flex justify-content-between align-items-center"
          @mousedown.prevent="startDragging"
        >
          <div class="d-flex align-items-center chatbot-header-center">
            <div class="chatbot-icon">
              <i class="bi bi-robot"></i>
            </div>
            <h5 class="ms-3 mb-0 chatbot-title">FoodCycle.ai Assistant</h5>
          </div>
          <div class="chatbot-controls">
            <!-- New Conversation -->
            <button class="btn btn-sm" @click="confirmResetConversation" title="Start New Conversation">
              <i class="bi bi-arrow-clockwise"></i>
            </button>
            <!-- Fullscreen Toggle -->
            <button class="btn btn-sm" @click="toggleFullscreen">
              <i :class="isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'"></i>
            </button>
            <!-- Minimize -->
            <button class="btn btn-sm" @click="toggleCollapse">
              <i class="bi bi-dash"></i>
            </button>
          </div>
        </div>

        <!-- Chatbot Content -->
        <div class="card-body chatbot-content overflow-auto" ref="chatContent">
          <!-- Messages -->
          <div
            v-for="(message, index) in chatbotStore.messages"
            :key="index"
            :class="['d-flex align-items-start mb-3', message.isUser ? 'flex-row-reverse' : '']"
          >
            <div
              class="chatbot-avatar"
              :class="{'ms-2': message.isUser, 'me-2': !message.isUser}"
            >
              <i :class="message.isUser ? 'bi bi-person-fill' : 'bi bi-robot'"></i>
            </div>
            <div class="chatbot-message" v-html="message.text"></div>
          </div>
          <!-- Typing Indicator -->
          <div v-if="chatbotStore.isSending" class="d-flex align-items-start mb-3">
            <div class="chatbot-avatar me-2">
              <i class="bi bi-robot"></i>
            </div>
            <div class="chatbot-message typing-indicator">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>

        <!-- Chatbot Footer -->
        <div class="card-footer chatbot-footer d-flex align-items-center">
          <textarea
            v-model="userMessage"
            ref="userInput"
            class="form-control chatbot-input"
            placeholder="Enter question here"
            @input="adjustTextareaHeight"
            @keydown.enter="handleEnterKey"
            :disabled="chatbotStore.isSending"
          ></textarea>
          <button class="btn chatbot-send-icon ms-2" @click="sendMessage">
            <i class="bi bi-send"></i>
          </button>
        </div>

        <!-- Resize Handles -->
        <div class="resize-handle top-left" @mousedown.prevent="startResizing('top-left')"></div>
        <div class="resize-handle top-right" @mousedown.prevent="startResizing('top-right')"></div>
        <div class="resize-handle bottom-left" @mousedown.prevent="startResizing('bottom-left')"></div>
        <div class="resize-handle bottom-right" @mousedown.prevent="startResizing('bottom-right')"></div>
      </div>
    </div>

    <!-- Reset Confirmation Modal -->
    <div v-if="showResetConfirmation" class="modal-backdrop" @click="showResetConfirmation = false">
      <div class="reset-confirmation-modal" @click.stop>
        <div class="modal-header">
          <h5>Start New Conversation?</h5>
        </div>
        <div class="modal-body">
          <p>Do you really want to reset the current conversation? All previous messages will be lost.</p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showResetConfirmation = false">Cancel</button>
          <button class="btn btn-primary" @click="resetConversation">Reset</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useAuthStore } from '../stores/auth';
import { useStepperStore } from '@/stores/stepper';
import { useLCAStore } from '@/stores/lca';
import { usePageContextStore } from '@/stores/pageContext';
import { useChatbotStore } from '@/stores/chatbot';

export default {
  name: "ChatBot",
  data() {
    return {
      userMessage: '',
      showResetConfirmation: false,
      // Set isCollapsed based on screen width: on large screens (>1024px) the chatbot is open by default
      isCollapsed: window.innerWidth > 1024 ? false : true,
      isFullscreen: false,
      width: 400,
      height: 600,
      position: { top: 'auto', left: 'auto', bottom: '20px', right: '20px' },
      initialPosition: null,
      initialMousePosition: null,
      resizing: false,
      dragging: false,
      currentResizeDirection: null,
    };
  },
  computed: {
    authStore() {
      return useAuthStore();
    },
    stepperStore() {
      return useStepperStore();
    },
    lcaStore() {
      return useLCAStore();
    },
    pageContextStore() {
      return usePageContextStore();
    },
    chatbotStore() {
      return useChatbotStore();
    },
    chatbotStyle() {
      if (this.isFullscreen || this.isSmallScreen()) {
        return {
          top: '0',
          left: '0',
          bottom: '0',
          right: '0',
          width: '100%',
          height: '100%',
        };
      } else {
        return {
          width: this.width + 'px',
          height: this.height + 'px',
          top: this.position.top,
          left: this.position.left,
          bottom: this.position.bottom,
          right: this.position.right,
        };
      }
    },
  },
  methods: {
    isSmallScreen() {
      return window.innerWidth <= 768;
    },
    async sendMessage() {
      if (this.userMessage.trim() !== '' && !this.chatbotStore.isSending) {
        this.chatbotStore.setIsSending(true);

        const messageToSend = this.userMessage;
        
        // Add user message to store
        this.chatbotStore.addMessage({ text: messageToSend, isUser: true });

        this.userMessage = '';
        this.adjustTextareaHeight();

        this.scrollToBottom();

        try {
          if (!this.authStore.isLoggedIn) {
            this.chatbotStore.addMessage({ text: 'You need to log in to use the chatbot.', isUser: false });
          } else {
            const requestData = {
              context: {
                userInputs: {
                  companyProfile: this.stepperStore.companyProfile,
                  wasteInfo: this.stepperStore.wasteInfo,
                  lcaData: {
                    lifecycleStage: this.lcaStore.lcaData.lifecycleStage,
                    selectedSolution: this.lcaStore.lcaData.selectedSolution,
                    selectedProvider: this.lcaStore.lcaData.selectedProvider,
                  },
                },
                userMessage: messageToSend,
                pageContext: this.pageContextStore.getPageContext,
              },
              thread_id: this.chatbotStore.threadId,
            };

            const response = await axios.post('/chatbot', requestData);
            console.log('Resolved URL:', response.config.baseURL + response.config.url);

            if (response.data.thread_id) {
              this.chatbotStore.setThreadId(response.data.thread_id);
            }

            this.chatbotStore.addMessage({ text: response.data.message, isUser: false });
          }
        } catch (error) {
          console.error('Error communicating with the backend:', error);
          this.chatbotStore.addMessage({ text: 'Sorry, there was a problem communicating with the server.', isUser: false });
        } finally {
          this.chatbotStore.setIsSending(false);
          this.scrollToBottom();
        }
      }
    },
    handleEnterKey(event) {
      if (event.shiftKey) {
        const textarea = this.$refs.userInput;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        this.userMessage =
          this.userMessage.slice(0, start) + "\n" + this.userMessage.slice(end);
        textarea.setSelectionRange(start + 1, start + 1);
        event.preventDefault();
        this.adjustTextareaHeight();
      } else {
        event.preventDefault();
        this.sendMessage();
      }
    },
    adjustTextareaHeight() {
      const textarea = this.$refs.userInput;
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const chatContent = this.$refs.chatContent;
        if (chatContent) {
          chatContent.scrollTop = chatContent.scrollHeight;
        }
      });
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        this.position = { top: '0', left: '0', bottom: '0', right: '0' };
      } else {
        this.position = { top: 'auto', left: 'auto', bottom: '20px', right: '20px' };
      }
    },
    startResizing(direction) {
      this.resizing = true;
      this.currentResizeDirection = direction;
      this.initialMousePosition = { x: event.clientX, y: event.clientY };
      this.initialSize = { width: this.width, height: this.height };
      this.initialPosition = { ...this.position };
      document.addEventListener('mousemove', this.resize);
      document.addEventListener('mouseup', this.stopResizing);
    },
    resize(event) {
      if (this.resizing && !this.isFullscreen) {
        const deltaX = event.clientX - this.initialMousePosition.x;
        const deltaY = event.clientY - this.initialMousePosition.y;

        const newWidth = this.initialSize.width + (this.currentResizeDirection.includes('left') ? -deltaX : deltaX);
        const newHeight = this.initialSize.height + (this.currentResizeDirection.includes('top') ? -deltaY : deltaY);

        this.width = Math.max(300, newWidth);
        this.height = Math.max(400, newHeight);

        if (this.currentResizeDirection.includes('left')) {
          const newLeft = parseInt(this.initialPosition.left) + deltaX;
          this.position.left = `${Math.min(Math.max(newLeft, 0), window.innerWidth - this.width)}px`;
        }

        if (this.currentResizeDirection.includes('top')) {
          const newTop = parseInt(this.initialPosition.top) + deltaY;
          this.position.top = `${Math.min(Math.max(newTop, 0), window.innerHeight - this.height)}px`;
        }
      }
    },
    stopResizing() {
      this.resizing = false;
      this.currentResizeDirection = null;
      document.removeEventListener('mousemove', this.resize);
      document.removeEventListener('mouseup', this.stopResizing);
    },
    startDragging(event) {
      if (!this.isFullscreen) {
        this.dragging = true;
        this.initialMousePosition = { x: event.clientX, y: event.clientY };
        const rect = event.currentTarget.parentElement.getBoundingClientRect();
        this.initialPosition = { top: rect.top, left: rect.left };
        document.addEventListener('mousemove', this.drag);
        document.addEventListener('mouseup', this.stopDragging);
      }
    },
    drag(event) {
      if (this.dragging) {
        const deltaX = event.clientX - this.initialMousePosition.x;
        const deltaY = event.clientY - this.initialMousePosition.y;

        const newTop = Math.max(0, this.initialPosition.top + deltaY);
        const newLeft = Math.max(0, this.initialPosition.left + deltaX);

        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;

        this.position.top = Math.min(newTop, windowHeight - this.height) + 'px';
        this.position.left = Math.min(newLeft, windowWidth - this.width) + 'px';

        this.position.bottom = 'auto';
        this.position.right = 'auto';
      }
    },
    stopDragging() {
      this.dragging = false;
      document.removeEventListener('mousemove', this.drag);
      document.removeEventListener('mouseup', this.stopDragging);
    },
    
    // Show confirmation dialog before resetting conversation
    confirmResetConversation() {
      if (this.chatbotStore.hasMessages) {
        this.showResetConfirmation = true;
      } else {
        this.resetConversation();
      }
    },
    
    // Reset conversation with animation
    resetConversation() {
      this.showResetConfirmation = false;
      
      // Add fade-out animation
      const chatContent = this.$refs.chatContent;
      if (chatContent) {
        chatContent.style.opacity = '0.3';
        chatContent.style.transition = 'opacity 0.3s ease';
      }
      
      setTimeout(() => {
        this.chatbotStore.clearMessages();
        
        // Restore opacity with fade-in
        if (chatContent) {
          chatContent.style.opacity = '1';
          this.scrollToBottom();
        }
        
        console.log('Conversation has been reset');
      }, 150);
    },
  },
  mounted() {
    this.adjustTextareaHeight();
  },
  beforeUnmount() {
    document.removeEventListener('mousemove', this.resize);
    document.removeEventListener('mouseup', this.stopResizing);
    document.removeEventListener('mousemove', this.drag);
    document.removeEventListener('mouseup', this.stopDragging);
  },
};
</script>

<style scoped>
.chatbot-minimized {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: rgba(135, 177, 64, 0.8);
  border-radius: 50%;
  width: 60px;
  height: 60px;
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 9999;
}

.chatbot-minimized-icon {
  color: #fff;
  font-size: 30px;
}

.chatbot-container {
  position: fixed;
  z-index: 9999;
  max-width: 100%;
  max-height: 100%;
  border-radius: 15px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.chatbot-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.chatbot-header {
  background-color: rgba(255, 255, 255, 0.9);
  color: #424242;
  padding: 0.5rem 1rem;
  cursor: move;
  user-select: none;
  border-bottom: 1px solid #e0e0e0;
}

.chatbot-header-center {
  flex: 1;
}

.chatbot-icon i {
  font-size: 28px;
  color: #87b140;
}

.chatbot-title {
  font-family: 'Arial', sans-serif;
  font-size: 1.2rem;
}

.chatbot-controls .btn {
  color: #424242;
}

.chatbot-controls .btn:hover {
  color: #87b140;
}

.chatbot-content {
  flex: 1;
  padding: 1rem;
  background-color: transparent;
}

.chatbot-avatar i {
  font-size: 24px;
  color: #87b140;
}

.chatbot-message {
  max-width: 75%;
  padding: 12px 16px;
  background: rgba(242, 245, 231, 0.9);
  border-radius: 12px;
  color: #333;
  word-wrap: break-word;
}

.chatbot-message.user {
  background: rgba(224, 224, 224, 0.9);
  color: #000;
}

.typing-indicator span {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 3px;
  background-color: #87b140;
  border-radius: 50%;
  opacity: 0;
  animation: typing 1.5s infinite;
}

@keyframes typing {
  0%, 100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

.chatbot-footer {
  background: rgba(255, 255, 255, 0.9);
  border-top: 1px solid #e0e0e0;
  padding: 0.75rem;
}

.chatbot-input {
  resize: none;
  height: auto;
  min-height: 40px;
  max-height: 150px;
  border: 1px solid #ccc;
  border-radius: 20px;
  padding: 10px;
  background-color: rgba(255, 255, 255, 0.8);
}

.chatbot-send-icon {
  background: #87b140;
  border-radius: 50%;
  color: #fff;
  transition: background-color 0.3s ease;
}

.chatbot-send-icon:hover {
  background: #75a236;
}

.chatbot-send-icon i {
  font-size: 22px;
}

.resize-handle {
  width: 20px;
  height: 20px;
  background: transparent;
  position: absolute;
  cursor: nwse-resize;
}

.resize-handle.top-left {
  top: 0;
  left: 0;
}

.resize-handle.top-right {
  top: 0;
  right: 0;
}

.resize-handle.bottom-left {
  bottom: 0;
  left: 0;
}

.resize-handle.bottom-right {
  bottom: 0;
  right: 0;
}

.chatbot-container.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  border-radius: 0;
  backdrop-filter: none;
}

.chatbot-container.is-fullscreen .resize-handle {
  display: none;
}

/* Reset Confirmation Modal */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(3px);
}

.reset-confirmation-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  width: 90%;
  margin: 20px;
  animation: modalSlideIn 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.reset-confirmation-modal .modal-header {
  padding: 20px 24px 10px;
  border-bottom: 1px solid #e0e0e0;
}

.reset-confirmation-modal .modal-header h5 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.reset-confirmation-modal .modal-body {
  padding: 20px 24px;
}

.reset-confirmation-modal .modal-body p {
  margin: 0;
  color: #666;
  line-height: 1.5;
}

.reset-confirmation-modal .modal-footer {
  padding: 15px 24px 20px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  border-top: 1px solid #e0e0e0;
}

.reset-confirmation-modal .btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.reset-confirmation-modal .btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
}

.reset-confirmation-modal .btn-secondary:hover {
  background: #e9ecef;
  color: #495057;
}

.reset-confirmation-modal .btn-primary {
  background: #87b140;
  color: white;
}

.reset-confirmation-modal .btn-primary:hover {
  background: #75a236;
}
</style>
